'use client';

import Image from 'next/image';
import { useSearchParams } from 'next/navigation';
import { useTranslations } from 'next-intl';
import { type Reducer, useCallback, useEffect, useReducer, useRef, useState } from 'react';
import { ConfirmDialog } from '@/app/components/shared/ConfirmDialog';
import { Card, CardContent } from '@/app/components/ui/card';
import { toast } from '@/app/components/ui/use-toast';
import {
  CONSENT_MARKETING_MSG_KEY,
  ENTERPRISE_COPY,
  ENTERPRISE_LOGO_URL,
  HOME_PAGE,
  isEnterpriseDistribution,
  SITE,
} from '@/app/constants';
import { useClaw, useCountdown, useCredits, useIsScreen, useSubscription, useTrials, useUserInfo } from '@/app/hooks';
import { useStore } from '@/app/providers/store';
import type { UserInfo } from '@/app/types';
import {
  cn,
  isPopupLoginSuccessMessage,
  POPUP_LOGIN_QUERY_KEY,
  POPUP_LOGIN_QUERY_VALUE,
  reportEvent,
} from '@/app/utils';
import { getDeviceId, safeStorage } from '@/app/utils/client';
import { getPathname, useLocale, usePathname, useRouter } from '@/i18n/routing';
import { CARD_SERIES_CONFIG } from '../constants';
import { useThirdPartyLogin, useValidateInviteCode, useVerifyCodeLogin } from '../hooks';
import { ViduWordmark } from '../icons/ViduWordmark';
import type { CardType, CardTypeAction, LoginInfo, ThirdPartyLoginAppType } from '../types';
import { getCustomPlatformLoginErrorMessage, getRedirectUrl } from '../utils';
import { IdentityCodeLoginCard } from './cardContent/IdentityCodeLoginCard';
import { LoginOptions } from './cardContent/LoginOptions';
import { LoginCardFooter } from './LoginCardFooter';
import { LoginCardTitle } from './LoginCardTitle';

export interface LoginProps {
  redirectAfterLogin?: string;
  className?: string;
  onLoginSuccess?: (userInfo: UserInfo) => void;
  presetCustomerCode?: string;
  presetTeamInviteCode?: string;
  /**
   * 撑满父容器高度（登录页分栏用）：不再按内容写死卡片高度，表单区垂直居中，底部协议文案吸底。
   * 弹窗等场景不传，仍按 cardHeight 固定高度。
   */
  fillHeight?: boolean;
  skipPostLoginInitialization?: boolean;
}

const handleCloseCustomPlatformDialog = () => {
  window.location.replace('/login');
};

export const Login = ({
  redirectAfterLogin,
  onLoginSuccess,
  className,
  presetCustomerCode,
  presetTeamInviteCode,
  fillHeight = false,
  skipPostLoginInitialization = false,
}: LoginProps = {}) => {
  const { userInfo } = useStore((state) => ({
    userInfo: state.userInfo,
  }));

  const [cardSeries, setCardSeries] = useState<CardType[]>([...CARD_SERIES_CONFIG]);

  const cardTypeReducer: Reducer<CardType, CardTypeAction> = (state, action) => {
    switch (action.type) {
      case 'next': {
        const currentIndex = cardSeries.indexOf(state);
        if (currentIndex < cardSeries.length - 1) {
          return cardSeries[currentIndex + 1] as CardType;
        }
        return state;
      }
      case 'back': {
        const currentIndex = cardSeries.indexOf(state);
        if (currentIndex > 0) {
          return cardSeries[currentIndex - 1] as CardType;
        }
        return state;
      }
      case 'reset': {
        return cardSeries[0];
      }
      default:
        return state;
    }
  };
  const [cardType, dispatch] = useReducer(cardTypeReducer, cardSeries[0]);

  useEffect(() => {
    dispatch({ type: 'reset' });
  }, [cardSeries]);
  const t = useTranslations();
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [errorMsg, setErrorMsg] = useState<string>('');
  const [codeError, setCodeError] = useState(false);
  const [inviteCode, setInviteCode] = useState('');
  const [inviteCodeError, setInviteCodeError] = useState('');
  const [inviteCodeType, setInviteCodeType] = useState('');
  const [enterpriseId, setEnterpriseId] = useState(presetCustomerCode?.trim() ?? '');
  const [enterpriseIdError, setEnterpriseIdError] = useState('');
  const [customPlatformDialogReason, setCustomPlatformDialogReason] = useState<string | null>(null);
  const router = useRouter();
  const locale = useLocale();
  const [consentMarketingMsg, setConsentMarketingMsg] = useState(false);
  const [agreedTerms, setAgreedTerms] = useState(SITE !== 'china');
  const showConsentMarketingMsg = useRef<boolean>(false);
  const [cardHeight, setCardHeight] = useState<number>(672);
  const [isWaitingThirdPartyPopupLogin, setIsWaitingThirdPartyPopupLogin] = useState(false);
  const searchParams = useSearchParams();
  const inviteCodeFromQuery = !isEnterpriseDistribution ? searchParams?.get('invite_code') || '' : '';
  const inviteBizTypeFromQuery = searchParams?.get('invite_biz_type') || '';
  const pathname = usePathname();
  // vidu-stream 相关页面触发登录时不提供邀请码入口（产品要求，直播场景不参与邀请拉新）
  const showInviteCode = !pathname?.startsWith('/vidu-stream');
  const popupLoginWindowRef = useRef<Window | null>(null);
  const loginInfo = useRef<LoginInfo>(
    SITE === 'china'
      ? {
          type: 'phone',
          value: '',
        }
      : {
          type: 'email',
          value: '',
        },
  );
  const { isLgScreen } = useIsScreen();
  const { getUserInfoAsync } = useUserInfo();
  const { updateCreditsAsync } = useCredits();
  const { updateSubscriptionAsync } = useSubscription();
  const { updateTrialsAsync } = useTrials();
  const { getMyClawsAsync } = useClaw();
  const teamInviteCode = presetTeamInviteCode?.trim() ?? '';
  const isEnterpriseIdPresetLocked = isEnterpriseDistribution && Boolean(teamInviteCode);
  const enterpriseIdReadOnly = isEnterpriseIdPresetLocked && Boolean(enterpriseId.trim());

  const [countdown, { startCountdown, stopCountdown }] = useCountdown({
    countStart: 0,
  });

  const refreshPostLoginState = useCallback(async () => {
    await Promise.all([
      updateSubscriptionAsync(),
      updateCreditsAsync(),
      updateTrialsAsync(),
      isEnterpriseDistribution ? undefined : getMyClawsAsync().catch(() => {}),
    ]);
  }, [updateSubscriptionAsync, updateCreditsAsync, updateTrialsAsync, getMyClawsAsync]);

  useEffect(() => {
    if (!userInfo) return;
    const directRedirectUrl = redirectAfterLogin?.trim();
    const queryRedirectUrl = searchParams?.get('redirect')?.trim();
    const redirectUrl = onLoginSuccess ? directRedirectUrl : directRedirectUrl || queryRedirectUrl;

    const hasRedirect = redirectUrl && redirectUrl.trim() !== '';

    onLoginSuccess?.(userInfo);

    if (onLoginSuccess && !hasRedirect) {
      if (isWaitingThirdPartyPopupLogin) {
        return;
      }
      return;
    }

    if (userInfo.is_first_login && !hasRedirect) {
      router.replace(HOME_PAGE);
    } else {
      router.replace(redirectUrl || getRedirectUrl(searchParams));
    }
  }, [userInfo, redirectAfterLogin, onLoginSuccess, router, searchParams, isWaitingThirdPartyPopupLogin]);

  // 企业成员态错误需要在 /login 页面和弹窗登录里保持同一反馈：弹窗提示后刷新页面。
  const handleCustomPlatformReason = useCallback((reason?: string) => {
    if (!isEnterpriseDistribution || !reason) {
      return false;
    }

    if (getCustomPlatformLoginErrorMessage(reason)) {
      setCustomPlatformDialogReason(reason);
      return true;
    }

    return false;
  }, []);

  const customPlatformDialogMessage = getCustomPlatformLoginErrorMessage(customPlatformDialogReason ?? undefined);

  const validateEnterpriseId = useCallback(() => {
    if (!isEnterpriseDistribution) {
      return true;
    }

    const nextEnterpriseId = enterpriseId.trim();
    if (nextEnterpriseId) {
      setEnterpriseIdError('');
      return true;
    }

    setEnterpriseIdError(ENTERPRISE_COPY.login.enterpriseIdRequired);
    return false;
  }, [enterpriseId, isEnterpriseDistribution]);

  const handleEnterpriseIdChange = useCallback((nextEnterpriseId: string) => {
    setEnterpriseId(nextEnterpriseId);
    if (nextEnterpriseId.trim()) {
      setEnterpriseIdError('');
    }
  }, []);

  useEffect(() => {
    showConsentMarketingMsg.current = safeStorage.getItem(CONSENT_MARKETING_MSG_KEY) !== 'true';
    setCardHeight((isLgScreen ? 560 : 672) + (SITE === 'global' && showConsentMarketingMsg.current ? 48 : 0));
  }, [isLgScreen]);

  const handleSetValidateResult = useCallback(({ error, code_type }: { error: string; code_type: string }) => {
    setInviteCodeError(error);
    setInviteCodeType(code_type);
  }, []);

  const { validateInviteCode } = useValidateInviteCode({
    setValidateResult: handleSetValidateResult,
  });

  // 记录上次已发起验证的码：失焦/Enter 重复触发时去重，避免验证 loading 翻转把登录按钮点击吞掉
  const lastValidatedInviteCodeRef = useRef<string>('');
  const handleValidateInviteCode = useCallback(
    (nextInviteCode: string) => {
      if (!nextInviteCode) {
        lastValidatedInviteCodeRef.current = '';
        setInviteCode('');
        setInviteCodeError('');
        return;
      }
      // 码未变化则不重复验证（点击控制台后再点登录按钮会触发第二次 onBlur）
      if (nextInviteCode === lastValidatedInviteCodeRef.current) {
        return;
      }
      lastValidatedInviteCodeRef.current = nextInviteCode;
      setInviteCode(nextInviteCode);
      validateInviteCode(nextInviteCode);
    },
    [validateInviteCode],
  );

  useEffect(() => {
    // 邀请码入口隐藏时也跳过 query 预填：否则验证失败的 inviteCodeError 无处展示，却会静默拦截登录
    if (!inviteCodeFromQuery || !showInviteCode) {
      return;
    }

    handleValidateInviteCode(inviteCodeFromQuery);

    const newSearchParams = new URLSearchParams(window.location.search);
    newSearchParams.delete('invite_code');
    const queryString = newSearchParams.toString();
    const newUrl = queryString ? `${window.location.pathname}?${queryString}` : window.location.pathname;
    window.history.replaceState(null, '', newUrl);
  }, [handleValidateInviteCode, inviteCodeFromQuery, showInviteCode]);

  const clearThirdPartyPopupLoginState = useCallback(() => {
    popupLoginWindowRef.current = null;
    setIsWaitingThirdPartyPopupLogin(false);
  }, []);

  const { thirdPartyLoading, signInWithThirdParty } = useThirdPartyLogin({
    onThirdPartyLoginSuccess: () => {
      if (showConsentMarketingMsg.current && consentMarketingMsg) {
        safeStorage.setItem(CONSENT_MARKETING_MSG_KEY, 'true');
        reportEvent('element_click', {
          element_name: 'email_ subscribe_ button_result',
          from_page: 'signup_page',
        });
      }
    },
    onThirdPartyPopupOpened: (popup) => {
      popupLoginWindowRef.current = popup;
      if (popup) {
        setIsWaitingThirdPartyPopupLogin(true);
      }
    },
  });

  useEffect(() => {
    if (!isWaitingThirdPartyPopupLogin) {
      return;
    }

    const handleThirdPartyLoginMessage = (event: MessageEvent<unknown>) => {
      const popupLoginWindow = popupLoginWindowRef.current;
      if (!popupLoginWindow) {
        return;
      }
      if (event.origin !== window.location.origin) {
        return;
      }
      if (event.source !== popupLoginWindow) {
        return;
      }
      if (!isPopupLoginSuccessMessage(event.data)) {
        return;
      }

      void (async () => {
        const { data } = await getUserInfoAsync({
          query: { is_first_login: false, skip_post_login_initialization: skipPostLoginInitialization },
        });
        if (!data) {
          return;
        }

        if (!skipPostLoginInitialization) {
          await refreshPostLoginState();
        }
        clearThirdPartyPopupLoginState();
      })();
    };

    window.addEventListener('message', handleThirdPartyLoginMessage);

    return () => {
      window.removeEventListener('message', handleThirdPartyLoginMessage);
    };
  }, [
    clearThirdPartyPopupLoginState,
    getUserInfoAsync,
    isWaitingThirdPartyPopupLogin,
    refreshPostLoginState,
    getMyClawsAsync,
    skipPostLoginInitialization,
  ]);

  useEffect(() => {
    return () => {
      clearThirdPartyPopupLoginState();
    };
  }, [clearThirdPartyPopupLoginState]);

  const { sending, sendVerifyCode, signInWithVerifyCode } = useVerifyCodeLogin({
    skipPostLoginInitialization,
    onSendVerifyCode: (success, errorMsg) => {
      if (success) {
        setErrorMsg('');
        startCountdown(60);
      } else {
        setErrorMsg(errorMsg);
      }
    },
    onSignInWithVerifyCode: (success, errorMsg) => {
      if (success) {
        if (showConsentMarketingMsg.current && consentMarketingMsg) {
          safeStorage.setItem(CONSENT_MARKETING_MSG_KEY, 'true');
          reportEvent('element_click', {
            element_name: 'email_ subscribe_ button_result',
            from_page: 'signup_page',
          });
        }
      } else {
        setErrorMsg(errorMsg);
      }
    },
    onSetCodeError: (codeError) => {
      setCodeError(codeError);
    },
    onCustomPlatformReason: handleCustomPlatformReason,
  });

  const handleSignInWithVerifyCode = (verifyCode: string) => {
    if (inviteCodeError || !validateEnterpriseId()) {
      return;
    }
    if (!isEnterpriseDistribution && !agreedTerms) {
      toast({ type: 'warning', message: '请先阅读并同意用户协议和隐私协议' });
      return;
    }
    signInWithVerifyCode({
      loginInfo: {
        type: cardType === 'PHONE' ? 'phone' : 'email',
        value: cardType === 'PHONE' ? `+86${phone}` : email,
        customerCode: isEnterpriseDistribution ? enterpriseId.trim() : undefined,
      },
      teamInviteCode,
      verifyCode,
      inviteCode: inviteCodeType === 'agent_share_viral' ? 'agent_share_viral_credit' : inviteCode,
      inviter_id:
        inviteCodeType === 'agent_share_viral' || inviteBizTypeFromQuery === 'artist_program' ? inviteCode : undefined,
      receive_marketing_msg: showConsentMarketingMsg.current ? consentMarketingMsg : undefined,
    });
  };

  const handleClickLoginNext = (type: CardType = 'EMAIL') => {
    if (inviteCodeError || !validateEnterpriseId()) {
      return;
    }
    if (!isEnterpriseDistribution && !agreedTerms) {
      toast({ type: 'warning', message: '请先阅读并同意用户协议和隐私协议' });
      return;
    }

    setCardSeries((prev) => {
      if (prev[1] === type) {
        return prev;
      }
      const next = [...prev];
      next[1] = type;
      return next;
    });
    dispatch({ type: 'next' });
    loginInfo.current = {
      type: type === 'EMAIL' ? 'email' : 'phone',
      value: '',
    };
    errorMsg && setErrorMsg('');
    if (type === 'EMAIL' && email) {
      setEmail('');
    }
    if (type === 'PHONE' && phone) {
      setPhone('');
    }
  };

  const handleClickIdentityNext = () => {
    if (inviteCodeError || !validateEnterpriseId()) {
      return;
    }
    if (!isEnterpriseDistribution && !agreedTerms) {
      toast({ type: 'warning', message: '请先阅读并同意用户协议和隐私协议' });
      return;
    }
    if (loginInfo.current.type === 'email') {
      loginInfo.current.value = email;
    } else if (loginInfo.current.type === 'phone') {
      loginInfo.current.value = `+86${phone}`;
    }
    // 企业标识只映射 IAM customer_code，不参与团队空间查找或切换。
    loginInfo.current.customerCode = isEnterpriseDistribution ? enterpriseId.trim() : undefined;
    if (sending || countdown > 0) {
      stopCountdown();
    }
    // 验证码与账号同屏，发送成功后停在当前页，倒计时由 onSendVerifyCode 启动
    sendVerifyCode(loginInfo.current);
  };

  const handleGoBack = () => {
    dispatch({ type: 'back' });
    if (errorMsg) {
      setErrorMsg('');
    }
  };

  const handleClickThirdPartyLogin = (type: ThirdPartyLoginAppType) => {
    if (inviteCodeError) {
      return;
    }
    if (!agreedTerms) {
      toast({ type: 'warning', message: '请先阅读并同意用户协议和隐私协议' });
      return;
    }
    const shouldUsePopupThirdPartyLogin = !!onLoginSuccess;
    const thirdPartyRedirectUrl = shouldUsePopupThirdPartyLogin
      ? (() => {
          const popupRedirectUrl = new URL(`${window.location.origin}${getPathname({ href: pathname, locale })}`);
          popupRedirectUrl.searchParams.set(POPUP_LOGIN_QUERY_KEY, POPUP_LOGIN_QUERY_VALUE);
          return popupRedirectUrl.toString();
        })()
      : getRedirectUrl(searchParams, `${window.location.origin}/${locale}`);
    signInWithThirdParty({
      url: thirdPartyRedirectUrl,
      type,
      device_id: getDeviceId(),
      inviteCode: inviteCodeType === 'agent_share_viral' ? 'agent_share_viral_credit' : inviteCode,
      inviter_id:
        inviteCodeType === 'agent_share_viral' || inviteBizTypeFromQuery === 'artist_program' ? inviteCode : undefined,
      receive_marketing_msg: showConsentMarketingMsg.current ? consentMarketingMsg : undefined,
      openInPopup: shouldUsePopupThirdPartyLogin,
    });
  };

  // 首屏之外的步骤在主按钮下方给「返回」，替代原先卡片左上角的箭头。
  const backAction = cardType !== cardSeries[0] ? handleGoBack : undefined;

  const renderCardContent = () => {
    switch (cardType) {
      case 'LOGIN':
        return (
          <LoginOptions
            handleClickThirdPartyLogin={handleClickThirdPartyLogin}
            handleClickLoginNext={handleClickLoginNext}
            // 邀请码为选填项，其验证 loading 不应禁用登录入口（无效码由各步骤 inviteCodeError 拦截）
            loading={thirdPartyLoading}
            handleValidateInviteCode={handleValidateInviteCode}
            inviteCodeError={inviteCodeError}
            inviteCode={inviteCode}
            showInviteCode={showInviteCode}
          />
        );
      case 'EMAIL':
      case 'PHONE': {
        const isPhone = cardType === 'PHONE';
        return (
          <IdentityCodeLoginCard
            // 切换账号类型时重置卡片内部的输入与校验状态
            key={cardType}
            identityType={isPhone ? 'phone' : 'email'}
            errorMsg={errorMsg}
            identity={isPhone ? phone : email}
            changeIdentity={isPhone ? setPhone : setEmail}
            sending={sending}
            second={countdown}
            sendVerifyCode={handleClickIdentityNext}
            handleSignInWithVerifyCode={handleSignInWithVerifyCode}
            codeError={codeError}
            onChangeCode={() => {
              if (!codeError) return;
              setCodeError(false);
              setErrorMsg('');
            }}
            inviteCode={inviteCode}
            handleValidateInviteCode={handleValidateInviteCode}
            inviteCodeError={inviteCodeError}
            showInviteCode={showInviteCode}
            enterpriseId={enterpriseId}
            enterpriseIdError={enterpriseIdError}
            onEnterpriseIdChange={handleEnterpriseIdChange}
            showEnterpriseId={isEnterpriseDistribution}
            enterpriseIdReadOnly={enterpriseIdReadOnly}
            onBack={backAction}
          />
        );
      }
    }
  };

  return (
    <>
      <Card
        className={cn(
          'max-h-screen w-[500px] bg-system-bg04/60 backdrop-blur-[50px] lg:w-[400px]',
          fillHeight && 'h-full',
          className,
        )}
        style={fillHeight ? undefined : { height: `${cardHeight}px` }}
      >
        {/* 登录页分栏（fillHeight）外层列已有左右留白，这里不再叠内边距，底部协议贴底。 */}
        <div className={cn('flex flex-1 flex-col', !fillHeight && 'px-4 py-6 lg:py-5')}>
          <CardContent
            className={cn('flex flex-1 flex-col items-center p-0 pt-8 pb-4 lg:pt-4', fillHeight && 'justify-center')}
          >
            {!isEnterpriseDistribution && (
              <ViduWordmark className="mb-3 text-[40px] text-white lg:mb-2 lg:text-[32px]" />
            )}
            {isEnterpriseDistribution && (
              <Image
                src={ENTERPRISE_LOGO_URL}
                alt="AI Vid"
                width={1056}
                height={354}
                priority
                className="mb-5 h-auto w-[168px] lg:mb-4 lg:w-[148px]"
              />
            )}

            <LoginCardTitle />
            {renderCardContent()}
          </CardContent>

          {!isEnterpriseDistribution && (
            <LoginCardFooter
              showConsentMarketingMsg={!!showConsentMarketingMsg.current}
              consentMarketingMsg={consentMarketingMsg}
              setConsentMarketingMsg={setConsentMarketingMsg}
              agreedTerms={agreedTerms}
              setAgreedTerms={setAgreedTerms}
            />
          )}
        </div>
      </Card>

      {customPlatformDialogMessage && (
        <ConfirmDialog
          title={ENTERPRISE_COPY.login.title}
          content={customPlatformDialogMessage}
          primaryButtonText={t('common.dialog.gotIt')}
          onPrimaryAction={handleCloseCustomPlatformDialog}
          onClose={handleCloseCustomPlatformDialog}
        />
      )}
    </>
  );
};
